import { useNavigation, useSearchParams } from 'react-router';
import { updateURLSearchParamsWithProjectContext } from '@gonfalon/app-context/updateURLSearchParamsWithProjectContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { useDebounce } from '@gonfalon/async/useDebounce';
import type { Statement } from '@gonfalon/audit-log/types';
import { useRouteActivitySpec } from '@gonfalon/audit-log/useRouteActivitySpec';
import { enableChangeHistory } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { ResourceSpecifier } from '@gonfalon/resource-specifiers/ast';
import { isEnvironmentScopedResource } from '@gonfalon/resource-specifiers/isEnvironmentScopedResource';
import { parse as resourceSpecifierFromString } from '@gonfalon/resource-specifiers/parser';
import { mergeSearchParams } from '@gonfalon/router/mergeSearchParams';
import { trackError } from '@gonfalon/telemetry/trackError';
import { useQueryClient } from '@tanstack/react-query';
import invariant from 'tiny-invariant';
import { z } from 'zod';

import { changeHistoryTracker, type DateFilterInteraction, endChangeHistoryExperienceIfActive } from './analytics';
import {
  type ActivityDateFilter,
  addResource,
  anyResource,
  CHANGE_HISTORY_QUERY_PARAMS,
  deserializeActivityFilter,
  resourceTypeFromStatement,
  serializeActivityFilter,
} from './ChangeHistory/ChangeHistoryUtils';
import { getFilterDateRange } from './internal/getFilterDateRange';
import { invalidateDependentQueries } from './internal/invalidateDependentQueries';

const statement = z.object({
  actions: z.array(z.string()).optional(),
  notActions: z.array(z.string()).optional(),
  effect: z.enum(['allow', 'deny']),
  resources: z.array(z.string()).optional(),
  notResources: z.array(z.string()).optional(),
});

export const useChangeHistory = () => {
  const projectContext = useProjectContext({ optional: true });
  const navigation = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeSpec = useRouteActivitySpec();
  const queryClient = useQueryClient();

  const filters = deserializeActivityFilter(searchParams);
  const isChangeHistoryVisible =
    navigation.state === 'loading' && navigation.location
      ? new URLSearchParams(navigation.location.search).has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY)
      : searchParams.has(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY);

  const optimisticSearchParams = navigation.location
    ? new URLSearchParams(navigation.location.search)
    : new URLSearchParams();
  const specParam = optimisticSearchParams.getAll(CHANGE_HISTORY_QUERY_PARAMS.SPEC);

  let spec =
    navigation.state === 'loading' && navigation.location
      ? specParam
          .map((s) => {
            try {
              return JSON.parse(decodeURIComponent(s));
            } catch {
              return '';
            }
          })
          .filter(Boolean)
      : filters.spec;
  spec = spec ?? routeSpec;

  const parsedResource = parsedResourceFromSpec(spec);

  // Snapshot of the filter state a handler is about to apply. Unlike the per-control
  // events, this captures the combinations users actually query with. Pass exactly what
  // the handler hands to serializeActivityFilter — omitting q there clears the search.
  const trackAppliedFilters = (next: {
    spec: unknown[] | undefined;
    q: string | undefined;
    dateMode: 'mostRecent' | 'range';
    sort: 'asc' | 'desc' | undefined;
  }) => {
    const nextSpec = next.spec ?? [];
    changeHistoryTracker.track('Apply change history filters', {
      filterCount: nextSpec.length,
      resourceTypes: [...new Set(nextSpec.map((s) => resourceTypeFromStatement(s) ?? 'unknown'))].sort(),
      hasSearch: Boolean(next.q),
      dateMode: next.dateMode,
      sort: next.sort,
    });
  };

  const currentDateMode = filters.after || filters.before ? 'range' : 'mostRecent';

  const open = (props?: {
    policy?: Statement[];
    after?: Date;
    before?: Date;
    sort?: 'asc' | 'desc';
    auditId?: string;
  }) => {
    if (!enableChangeHistory()) {
      return;
    }

    const startAttributes: {
      resourceType?: string;
      isList?: boolean;
      openAuditDetail?: boolean;
    } = {};
    if (parsedResource) {
      startAttributes.resourceType = parsedResource.type;
      if ('name' in parsedResource) {
        startAttributes.isList = parsedResource.name === anyResource;
      }
    }
    if (props?.auditId) {
      startAttributes.openAuditDetail = true;
    }
    changeHistoryTracker.start(startAttributes);

    let dateFilter: ActivityDateFilter = { mode: 'mostRecent' };
    if (props?.before || props?.after) {
      const { start, end } = getFilterDateRange(props?.before, props?.after);
      dateFilter = { mode: 'range', after: start, before: end };
    }
    trackAppliedFilters({
      spec: props?.policy ?? routeSpec,
      q: undefined,
      dateMode: dateFilter.mode,
      // serializeActivityFilter keeps the URL's sort when open() doesn't specify one
      sort: props?.sort ?? filters.sort,
    });
    const filtersFromSpecParams = serializeActivityFilter(
      {
        spec: props?.policy ?? routeSpec,
        dateFilter,
        sort: props?.sort,
      },
      searchParams,
    );

    const mergedParams = mergeSearchParams(searchParams, filtersFromSpecParams);

    if (props?.auditId) {
      mergedParams.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID, props.auditId);
      // Mark as detail-only view (no list context to return to)
      mergedParams.set(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_DETAIL_ONLY, 'true');
    }

    setSearchParams(mergedParams);
  };

  const close = (nextParams?: URLSearchParams) => {
    endChangeHistoryExperienceIfActive('cancel');
    const next = nextParams ? new URLSearchParams([...nextParams, ...searchParams]) : new URLSearchParams(searchParams);
    Object.values(CHANGE_HISTORY_QUERY_PARAMS).forEach((key) => {
      next.delete(key);
    });
    setSearchParams(next);

    const resourceKeys = resourceKeysFromParsedResource(parsedResource);
    if (resourceKeys) {
      void invalidateDependentQueries(queryClient, resourceKeys);
    }
  };

  const deleteResourceFilter = (index: number): void => {
    invariant(spec, 'spec should be defined');

    changeHistoryTracker.track('Delete resource filter', { resourceType: resourceTypeFromStatement(spec[index]) });

    const newSpec = [...spec];
    newSpec.splice(index, 1);

    trackAppliedFilters({ spec: newSpec, q: undefined, dateMode: currentDateMode, sort: filters.sort });

    const nextFilter = serializeActivityFilter(
      {
        spec: newSpec,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const addResourceFilter = (resource: ResourceSpecifier['type']): void => {
    invariant(spec, 'spec should be defined');

    changeHistoryTracker.track('Add resource', { resourceType: resource });

    const nextStatement = addResource(
      resource,
      projectContext?.project?.key || '*',
      projectContext?.selectedEnvironment?.key ? [projectContext.selectedEnvironment.key] : ['*'],
    );
    const newSpec = [...spec];
    newSpec.push(nextStatement);

    trackAppliedFilters({ spec: newSpec, q: undefined, dateMode: currentDateMode, sort: filters.sort });

    const nextFilter = serializeActivityFilter(
      {
        spec: newSpec,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const auditLogIdParam = optimisticSearchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID);
  const auditId =
    navigation.state === 'loading' && auditLogIdParam
      ? auditLogIdParam
      : searchParams.get(CHANGE_HISTORY_QUERY_PARAMS.ACTIVITY_AUDIT_ID);

  const updateResourceFilter = (index: number, nextStatement: Statement) => {
    invariant(spec, 'spec should be defined');

    const newSpec = [...spec];

    newSpec[index] = nextStatement;

    trackAppliedFilters({ spec: newSpec, q: undefined, dateMode: currentDateMode, sort: filters.sort });

    const nextFilter = serializeActivityFilter(
      {
        spec: newSpec,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const resetResourceFilter = (index: number, resource: ResourceSpecifier['type']) => {
    const nextStatement = addResource(
      resource,
      projectContext?.project?.key || '*',
      projectContext?.selectedEnvironment?.key ? [projectContext.selectedEnvironment.key] : ['*'],
    );
    updateResourceFilter(index, nextStatement);
  };

  const updateTextFilter_ = (text: string) => {
    changeHistoryTracker.track('Update search filter', { hasValue: text.length > 0 });
    trackAppliedFilters({ spec, q: text, dateMode: currentDateMode, sort: filters.sort });

    const nextFilter = serializeActivityFilter(
      {
        ...filters,
        spec,
        q: text,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const updateTextFilter = useDebounce(updateTextFilter_, 150);

  const updateDateFilter = (dateFilter: ActivityDateFilter, interaction?: DateFilterInteraction) => {
    changeHistoryTracker.track('Update date filter', { mode: dateFilter.mode, ...interaction });
    trackAppliedFilters({ spec, q: filters.q, dateMode: dateFilter.mode, sort: filters.sort });

    const nextFilter = serializeActivityFilter(
      {
        ...filters,
        spec,
        dateFilter,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  const updateMemberFilter = (members: string[]) => {
    changeHistoryTracker.track('Update member filter', { hasValue: members.length > 0 });

    const nextFilter = serializeActivityFilter(
      {
        ...filters,
        spec,
        members,
      },
      searchParams,
    );

    if (projectContext) {
      const nextParams = updateURLSearchParamsWithProjectContext(nextFilter, projectContext.context);
      setSearchParams(nextParams);
    } else {
      setSearchParams(nextFilter);
    }
  };

  return {
    hasRouteSpec: routeSpec !== undefined,
    spec,
    filters,
    open,
    close,
    isChangeHistoryVisible,
    addResourceFilter,
    updateResourceFilter,
    deleteResourceFilter,
    resetResourceFilter,
    updateTextFilter,
    updateDateFilter,
    updateMemberFilter,
    auditId,
    parsedResource,
  };
};

function parsedResourceFromSpec(spec: unknown): ResourceSpecifier | undefined {
  try {
    const parsedSpec = statement.array().safeParse(spec);
    if (parsedSpec.success && parsedSpec.data.length > 0) {
      const [firstResource] = parsedSpec.data[0].resources ?? [];

      if (firstResource) {
        const parsedResourceSpecifier = resourceSpecifierFromString(firstResource);
        if (parsedResourceSpecifier.ok) {
          return parsedResourceSpecifier.val;
        }
      }
    }
  } catch (error) {
    if (error instanceof Error) {
      trackError(error, { severity: 'medium', owner: 'team-foundation' });
    }
  }

  return undefined;
}

function resourceKeysFromParsedResource(resource: ResourceSpecifier | undefined) {
  if (!resource) {
    return undefined;
  }
  const environment = isEnvironmentScopedResource(resource) ? resource.environment : undefined;
  const projectKey = environment?.project.name ?? '';
  const environmentKey = environment?.name ?? '';
  const resourceKey = resource.type === 'acct' || resource.type === 'ip-allowlist' ? anyResource : resource.name;
  if (
    !projectKey ||
    !environmentKey ||
    !resourceKey ||
    projectKey === anyResource ||
    environmentKey === anyResource ||
    resourceKey === anyResource
  ) {
    return undefined;
  }
  return { projectKey, environmentKey, resourceKey };
}
